import { useAccounts, useAccountStore } from '@tankef/core-react';
import { useTranslation } from '@tankef/i18n';
import { Select } from '@tankef/ui';

import { useLocalFlag } from '@/shared/featureFlags/useLocalFlag';

/** Etiqueta compacta de una cuenta: "Cuenta ••••XXXX" (últimos 4 de la CLABE). */
function accountLabel(clabe: string): string {
  const last4 = clabe.slice(-4);
  return `Cuenta ••••${last4}`;
}

/**
 * Switcher de cuenta en el header (PM multi-cuenta, F7.4 — UI mínima). Solo se
 * muestra cuando el flag PM está on y hay 2+ cuentas (con una sola no hay nada
 * que cambiar). El `Account` no expone nombre → etiquetamos por CLABE.
 *
 * Escribe la selección en `useAccountStore`; los consumidores (e.g.
 * AccountUsersSection) hacen fallback a la cuenta primaria si es null.
 */
export function AccountSwitcher() {
  const { t } = useTranslation('common');
  const moralEnabled = useLocalFlag('onboarding_moral_enabled', true);
  const { data: accounts } = useAccounts();
  const currentAccountId = useAccountStore(s => s.currentAccountId);
  const setCurrentAccountId = useAccountStore(s => s.setCurrentAccountId);

  if (!moralEnabled || !accounts || accounts.length < 2) {
    return null;
  }

  const defaultId = accounts.find(a => a.isDefault)?.id ?? accounts[0]?.id ?? '';
  const effectiveId = currentAccountId ?? defaultId;

  return (
    <div className="w-44" data-testid="account-switcher">
      <Select
        aria-label={t('toolbar.switchAccount')}
        options={accounts.map(a => ({ value: a.id, label: accountLabel(a.clabe) }))}
        value={effectiveId}
        onChange={e => setCurrentAccountId(e.target.value)}
      />
    </div>
  );
}
